// src/components/GenericCrud.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dayjs from "dayjs";
import { useDataProvider, useNotification } from "@refinedev/core";
import {
  Table,
  Space,
  Button,
  Input,
  Modal,
  Form,
  DatePicker,
  Checkbox,
  InputNumber,
  Drawer,
  Dropdown,
  Result,
  Select,
  Grid,
  theme as antdTheme,
  Flex,
  Typography,
  Tag,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  SaveOutlined,
} from "@ant-design/icons";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  linkPlugin,
  linkDialogPlugin,
  markdownShortcutPlugin,
  toolbarPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  ListsToggle,
  CreateLink,
  InsertThematicBreak,
} from "@mdxeditor/editor";

import {
  RESOURCES,
  type ResourceConfig,
  type ResourceField,
  type ResourceAction,
} from "../resources/config";
import { downloadFile } from "../utils/downloadFile";
import { usePGBroadcastRefresh } from "../realtime/usePGBroadcastRefresh";

const { TextArea } = Input;
const { useBreakpoint } = Grid;
const { Text } = Typography;

// --- Helpers ---


// Start Color

// --- Value color mapper (dual-mode: AntD Tag or themed Pill) ---

type BadgeRenderMode = "tag" | "pill";

const VALUE_BADGE_MODE: BadgeRenderMode = "tag"; // <-- switch: "tag" or "pill"

type ValueBadgeStyle = {
  // Pill styling (only used when mode === "pill")
  fg?: string;
  bg?: string;
  border?: string;
  fontWeight?: number;

  // Tag styling (only used when mode === "tag")
  tagColor?: string; // e.g. "green" | "red" | "gold" | "blue" | "cyan" | ...
};

type ValueBadgeRule = {
  fields?: string[];
  kinds?: Array<ResourceField["kind"] | "markdown" | "any">;

  equalsAny?: Array<string | number | boolean>;
  equalsCIAny?: string[];
  regex?: RegExp;

  display?: string;

  style: ValueBadgeStyle;
};

// Ant Tag color options are flexible; these are common + look good in dark mode too.
const VALUE_BADGE_RULES: ValueBadgeRule[] = [
/*
  // If you want it to apply to ALL columns:
  {
  kinds: ["any"],
  regex: /^-?\d+(\.\d+)?$/, // only a number
  style: { tagColor: "gold", fontWeight: 600 },
},
*/

{
  fields: ["port", "count"],
  regex: /^-?\d+(\.\d+)?$/,
  style: { tagColor: "gold", fontWeight: 600 },
},

  {
    kinds: ["any"],
    equalsCIAny: ["true", "yes", "y", "enabled", "active", "ok", "success"],
    style: {
      tagColor: "green",
      fg: "#135200",
      bg: "#f6ffed",
      border: "#b7eb8f",
      fontWeight: 600,
    },
  },
  {
    kinds: ["any"],
    equalsCIAny: ["false", "no", "n", "disabled", "inactive", "error", "fail", "failed"],
    style: {
      tagColor: "red",
      fg: "#a8071a",
      bg: "#fff1f0",
      border: "#ffa39e",
      fontWeight: 600,
    },
  },
  {
    kinds: ["any"],
    equalsCIAny: ["pending", "queued", "waiting"],
    style: {
      tagColor: "gold",
      fg: "#874d00",
      bg: "#fff7e6",
      border: "#ffd591",
      fontWeight: 600,
    },
  },

  // Add your own:
  // { fields: ["status"], equalsCIAny: ["running"], style: { tagColor: "blue", ... } },
];

function toComparableText(v: any): string {
  if (v == null) return "";
  if (typeof v === "string") return v.trim();
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
}

function ruleAppliesToField(rule: ValueBadgeRule, field: ResourceField) {
  const kind = (field as any).kind === "markdown" ? "markdown" : field.kind;
  const kinds = rule.kinds || ["any"];
  const kindOk = kinds.includes("any") || kinds.includes(kind as any);
  const fieldOk = !rule.fields || rule.fields.includes(field.source);
  return kindOk && fieldOk;
}

function matchesRule(rule: ValueBadgeRule, value: any): { matched: boolean; display?: string } {
  if (rule.equalsAny && rule.equalsAny.some((x) => x === value)) {
    return { matched: true, display: rule.display };
  }

  const text = toComparableText(value);
  if (!text) return { matched: false };

  if (rule.equalsCIAny) {
    const t = text.toLowerCase();
    if (rule.equalsCIAny.some((x) => x.toLowerCase() === t)) {
      return { matched: true, display: rule.display };
    }
  }

  if (rule.regex && rule.regex.test(text)) {
    return { matched: true, display: rule.display };
  }

  return { matched: false };
}

function canBadgeNode(rendered: React.ReactNode): rendered is string | number {
  return typeof rendered === "string" || typeof rendered === "number";
}

// This is the one function you call from your column render.
// It uses theme tokens for pill mode, so it behaves in light/dark.
function renderValueBadge(
  token: any,
  field: ResourceField,
  value: any,
  rendered: React.ReactNode,
): React.ReactNode {
  const simpleText = canBadgeNode(rendered)
    ? String(rendered)
    : (typeof value === "string" || typeof value === "number" || typeof value === "boolean")
      ? String(value)
      : null;

  if (!simpleText) return rendered;


  // --- Field-level overrides from config.ts ---
  const fieldTagColor =
    (field as any).tagColor || (field as any).color; // allow "color" alias

  if (fieldTagColor) {
    if (VALUE_BADGE_MODE === "tag") {
      return (
        <Tag
          color={fieldTagColor}
          style={{
            marginInlineEnd: 0,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {simpleText}
        </Tag>
      );
    }

    // pill mode: allow bg/text overrides; else use theme tokens
    const fg = (field as any).textColor ?? token.colorText;
    const bg = (field as any).bgColor ?? token.colorFillQuaternary;
    const border = token.colorBorderSecondary;

    return (
      <span
        style={{
          display: "inline-block",
          padding: "0px 8px",
          lineHeight: "20px",
          borderRadius: 999,
          border: `1px solid ${border}`,
          background: bg,
          color: fg,
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {simpleText}
      </span>
    );
  }


  for (const rule of VALUE_BADGE_RULES) {
    if (!ruleAppliesToField(rule, field)) continue;
    const m = matchesRule(rule, value);
    if (!m.matched) continue;

    const text = m.display ?? simpleText;

    // --- Tag mode ---
    if (VALUE_BADGE_MODE === "tag") {
      const c = rule.style.tagColor || "default";
      return (
        <Tag
          color={c}
          style={{
            marginInlineEnd: 0, // helps in tight tables
            fontWeight: rule.style.fontWeight ?? 600,
            whiteSpace: "nowrap",
          }}
        >
          {text}
        </Tag>
      );
    }

    // --- Pill mode (theme-aware) ---
    // If rule supplies explicit colors, use them; otherwise fall back to theme tokens.
    const fg = rule.style.fg ?? token.colorText;
    const bg = rule.style.bg ?? token.colorFillQuaternary;
    const border = rule.style.border ?? token.colorBorderSecondary;

    return (
      <span
        style={{
          display: "inline-block",
          padding: "0px 8px",
          lineHeight: "20px",
          borderRadius: 999,
          border: `1px solid ${border}`,
          background: bg,
          color: fg,
          fontWeight: rule.style.fontWeight ?? 600,
          whiteSpace: "nowrap",
        }}
      >
        {text}
      </span>
    );
  }

  return rendered;
}



// End Color

type RowActionContext = {
  record: any;
  selectedRowKeys: React.Key[];
};

function isRowActionVisible(
  action: ResourceAction,
  ctx: RowActionContext,
): boolean {
  const anyAction = action as any;

  // Optional config: visibleIf: (ctx) => boolean
  if (typeof anyAction.visibleIf === "function") {
    try {
      return !!anyAction.visibleIf(ctx);
    } catch {
      return false;
    }
  }

  // Optional config: hiddenIf: (ctx) => boolean
  if (typeof anyAction.hiddenIf === "function") {
    try {
      return !anyAction.hiddenIf(ctx);
    } catch {
      return true;
    }
  }

  return true;
}

const isLinkTableField = (f: ResourceField) => (f as any).kind === "linkTable";

const UI = {
  REFOCUS_TOP_ON_CLOSE: true,
};

const TopFocusAnchor: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const timer = setTimeout(() => {
      ref.current?.focus({ preventScroll: false });
    }, 50);
    return () => clearTimeout(timer);
  }, []);
  return (
    <div
      ref={ref}
      tabIndex={-1}
      style={{ outline: "none", height: 0, width: 0, overflow: "hidden" }}
      aria-hidden="true"
    />
  );
};

type RefOption = { value: any; label: string };

type DisplayOnlyFieldProps = {
  field: ResourceField;
  form: any;
  refOptions: Record<string, RefOption[]>;
};

const DisplayOnlyField: React.FC<DisplayOnlyFieldProps> = ({
  field,
  form,
  refOptions,
}) => {
  const valueKey = ((field as any).valueFrom as string) || field.source;
  const watched = Form.useWatch(valueKey, form);

  let displayValue: any = watched;
  const ref = (field as any).ref;

  if (ref && watched != null) {
    const opts = refOptions[field.source] || refOptions[valueKey] || [];
    const opt = opts.find((o) => o.value === watched);
    displayValue = opt ? opt.label : watched;
  }

  return (
    <Form.Item key={field.source} label={field.label || field.source}>
      <Input
        value={displayValue != null ? String(displayValue) : ""}
        disabled
      />
    </Form.Item>
  );
};

type PromptArgConfig = {
  name: string;
  label: string;
  optional?: boolean;
  defaultValue?: any;
  kind?: "text" | "select" | "password";
  ref?: {
    resource: string;
    optionText?: string;
    schema?: string;
  };
  asTextArray?: boolean;
};

type ActionPromptState = {
  open: boolean;
  action: ResourceAction | null;
  record: any | null;
  args: PromptArgConfig[];
  options: Record<string, RefOption[]>;
};

type GenericCrudProps = {
  resource: string;
  mobile?: boolean;
};

const isBooleanField = (f: ResourceField) => f.kind === "boolean";
const isNumberField = (f: ResourceField) => f.kind === "number";
const isDateField = (f: ResourceField) => f.kind === "date";
const isLongTextField = (f: ResourceField) => f.kind === "longtext";
const isSelectField = (f: ResourceField) => f.kind === "select";
const isMarkdownField = (f: ResourceField) => (f as any).kind === "markdown";

const renderFieldValue = (field: ResourceField, value: any) => {
  if (value == null) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (isBooleanField(field)) return value ? "yes" : "no";
  if (isNumberField(field)) return String(value);
  if (isDateField(field)) return String(value).replace("T", " ").slice(0, 19);
  return String(value);
};

function normalizeValuesWithFields(
  vals: Record<string, any>,
  formFields: ResourceField[],
) {
  const out: Record<string, any> = { ...(vals || {}) };

  for (const f of formFields) {
    const k = f.source;

    if ((f as any).displayOnly || (f as any).readOnly) {
      delete out[k];
      continue;
    }

    if (!(k in out)) continue;

    const v = out[k];

    if (v === undefined) {
      delete out[k];
      continue;
    }

    if (
      isDateField(f) &&
      v &&
      typeof v === "object" &&
      typeof (v as any).toISOString === "function"
    ) {
      out[k] = (v as any).toISOString();
      continue;
    }

    if (isBooleanField(f)) {
      if (v === 0 || v === 1) out[k] = !!v;
      else if (v === "true") out[k] = true;
      else if (v === "false") out[k] = false;
      continue;
    }

    if (typeof v === "string") {
      const trimmed = v.trim();
      out[k] = trimmed === "" ? null : trimmed;
      continue;
    }

    if (Array.isArray(v)) {
      out[k] = v.map((x) => (typeof x === "string" ? x.trim() : x));
      continue;
    }
  }

  return out;
}

type MarkdownFieldProps = {
  form: any;
  field: ResourceField;
  rules: any[];
  disabled: boolean;
  resourceName: string;
};

const mdModeStorageKey = (resource: string, fieldSource: string) =>
  `mdMode:${resource}:${fieldSource}`;

const MarkdownField: React.FC<MarkdownFieldProps> = ({
  form,
  field,
  rules,
  disabled,
  resourceName,
}) => {
  const { token } = antdTheme.useToken();
  const watched = Form.useWatch(field.source, form);
  const value = watched != null ? String(watched) : "";

  const [mode, setMode] = React.useState<string>(() => {
    try {
      const saved = localStorage.getItem(
        mdModeStorageKey(resourceName, field.source),
      );
      if (saved === "raw" || saved === "preview" || saved === "raw-preview")
        return saved;
      return "rich";
    } catch {
      return "rich";
    }
  });

  const setAndPersistMode = (m: string) => {
    setMode(m);
    try {
      localStorage.setItem(mdModeStorageKey(resourceName, field.source), m);
    } catch {}
  };

  const effectiveMode = disabled ? "preview" : mode;
  const showRich = effectiveMode === "rich";
  const showRaw = effectiveMode === "raw" || effectiveMode === "raw-preview";
  const showPreview =
    effectiveMode === "preview" || effectiveMode === "raw-preview";

  const isDark = useMemo(() => {
    return (
      token.colorBgContainer === "#141414" ||
      token.colorBgContainer === "#000000" ||
      document.body.classList.contains("app-dark")
    );
  }, [token]);

  const rows = (field as any).rows ?? 8;
  const minHeight = rows * 24;

  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 6,
        }}
      >
        <label
          className="ant-form-item-label"
          style={{
            color: token.colorText,
            fontSize: 14,
            display: "block",
          }}
        >
          <span
            className={
              rules.some((r) => r.required)
                ? "ant-form-item-required"
                : undefined
            }
          >
            {field.label || field.source}
          </span>
        </label>

        <Select
          value={mode}
          onChange={setAndPersistMode}
          size="small"
          style={{ width: 160 }}
          disabled={disabled}
          options={[
            { label: "Rich Editor", value: "rich" },
            { label: "Raw Markdown", value: "raw" },
            { label: "Raw + Preview", value: "raw-preview" },
            { label: "Preview Only", value: "preview" },
          ]}
        />
      </div>

      <Form.Item name={field.source} rules={rules} noStyle>
        <Input type="hidden" />
      </Form.Item>

      <div
        style={{
          border: `1px solid ${token.colorBorder}`,
          borderRadius: token.borderRadius,
          backgroundColor: showRaw ? token.colorBgContainer : "transparent",
          overflow: "hidden",
        }}
      >
        {showRaw && (
          <TextArea
            value={value}
            onChange={(e) =>
              form.setFieldsValue({ [field.source]: e.target.value })
            }
            rows={rows}
            disabled={disabled}
            placeholder={(field as any).placeholder ?? "Markdown supported"}
            style={{
              fontFamily: "monospace",
              border: "none",
              borderRadius: 0,
              boxShadow: "none",
              resize: "vertical",
              minHeight: minHeight,
              backgroundColor: "transparent",
              color: token.colorText,
              borderBottom: showPreview
                ? `1px solid ${token.colorBorderSecondary}`
                : "none",
            }}
          />
        )}

        {showRich && (
          <div
            className={isDark ? "dark-theme" : ""}
            style={
              {
                minHeight: minHeight,
                backgroundColor: token.colorBgContainer,
                "--mdx-base-color": token.colorText,
                "--mdx-text-color": token.colorText,
                "--mdx-bg-color": token.colorBgContainer,
                "--mdx-border-color": token.colorBorder,
                "--mdx-toolbar-bg-color": token.colorFillQuaternary,
                "--mdx-toolbar-text-color": token.colorTextDescription,
                "--mdx-toolbar-active-bg-color": token.colorFillSecondary,
                "--mdx-toolbar-hover-bg-color": token.colorFillTertiary,
                "--mdx-dropdown-bg-color": token.colorBgElevated,
                "--mdx-dropdown-text-color": token.colorText,
              } as React.CSSProperties
            }
          >
            <MDXEditor
              key={`mdx-${isDark ? "dark" : "light"}`}
              markdown={value}
              readOnly={disabled}
              onChange={(next) => form.setFieldsValue({ [field.source]: next })}
              contentEditableClassName="prose"
              plugins={[
                headingsPlugin(),
                listsPlugin(),
                quotePlugin(),
                thematicBreakPlugin(),
                linkPlugin(),
                linkDialogPlugin(),
                markdownShortcutPlugin(),
                toolbarPlugin({
                  toolbarContents: () => (
                    <>
                      <UndoRedo />
                      <BoldItalicUnderlineToggles />
                      <ListsToggle />
                      <CreateLink />
                      <InsertThematicBreak />
                    </>
                  ),
                }),
              ]}
            />
          </div>
        )}

        {showPreview && (
          <div
            style={{
              padding: "12px",
              minHeight: showRaw ? 120 : minHeight,
              maxHeight: 500,
              overflowY: "auto",
              backgroundColor: token.colorFillQuaternary,
              color: token.colorText,
            }}
          >
            {effectiveMode === "raw-preview" && (
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  marginBottom: 8,
                  textTransform: "uppercase",
                  color: token.colorTextDescription,
                  opacity: 0.8,
                }}
              >
                Live Preview
              </div>
            )}

            {value.trim() ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({ node, ...props }) => (
                    <p style={{ color: token.colorText }} {...props} />
                  ),
                  li: ({ node, ...props }) => (
                    <li style={{ color: token.colorText }} {...props} />
                  ),
                  h1: ({ node, ...props }) => (
                    <h1 style={{ color: token.colorTextHeading }} {...props} />
                  ),
                  h2: ({ node, ...props }) => (
                    <h2 style={{ color: token.colorTextHeading }} {...props} />
                  ),
                  h3: ({ node, ...props }) => (
                    <h3 style={{ color: token.colorTextHeading }} {...props} />
                  ),
                  a: ({ node, ...props }) => (
                    <a style={{ color: token.colorLink }} {...props} />
                  ),
                }}
              >
                {value}
              </ReactMarkdown>
            ) : (
              <span
                style={{
                  color: token.colorTextPlaceholder,
                  fontStyle: "italic",
                }}
              >
                Nothing to preview
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper to update a specific item in an array
function updateItem<T>(arr: T[], index: number, newItem: T) {
  const copy = [...arr];
  copy[index] = newItem;
  return copy;
}

type LinkTableEditorProps = {
  field: ResourceField;
  dataProvider: any;
  notify: (args: {
    type?: "success" | "error" | "warning" | "info";
    message: string;
    description?: string;
  }) => void;
};

function LinkTableEditor({
  field,
  dataProvider,
  notify,
}: LinkTableEditorProps) {
  const screens = Grid.useBreakpoint();
  const isXS = !!screens.xs;

  const { token } = antdTheme.useToken();
  const {
    colorBorderSecondary,
    colorBgContainer,
    colorBgElevated,
    colorText,
    colorTextSecondary,
  } = token;

  const cfgLT = (field as any).linkTable;
  const label = cfgLT?.label || field.label || field.source;
  const extraFields = cfgLT?.extraFields || [];

  // We store the entire junction record in links so we can edit attributes
  const [links, setLinks] = useState<any[]>([]);
  const [allTargets, setAllTargets] = useState<RefOption[]>([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [toAdd, setToAdd] = useState<any>(undefined);

  const leftId = (field as any)._recordId;

  const junctionResource = cfgLT?.junction?.resource;
  const junctionSchema = cfgLT?.junction?.schema;
  const leftKey = cfgLT?.leftKey;
  const rightKey = cfgLT?.rightKey;

  const targetResource = cfgLT?.target?.resource;
  const targetSchema = cfgLT?.target?.schema;
  const targetIdField = cfgLT?.target?.idField || "id";
  const optionText = cfgLT?.target?.optionText || "name";

  const canUse =
    !!leftId &&
    !!junctionResource &&
    !!leftKey &&
    !!rightKey &&
    !!targetResource &&
    !!dataProvider?.getList;

  const load = useCallback(async () => {
    if (!canUse) return;

    setLoadingLinks(true);
    try {
      // 1) Load junction rows
      const jRes: any = await dataProvider.getList({
        resource: junctionResource,
        pagination: { current: 1, pageSize: 2000 },
        filters: [{ field: leftKey, operator: "eq", value: leftId }],
        // If you renamed created_at and this errors, remove sorters.
        sorters: [{ field: "created_at", order: "desc" }],
        meta: junctionSchema ? { schema: junctionSchema } : undefined,
      });

      const junctionRows = (jRes?.data ?? []) as any[];

      // 2) Load target options
      const tRes: any = await dataProvider.getList({
        resource: targetResource,
        pagination: { current: 1, pageSize: 5000 },
        meta: targetSchema ? { schema: targetSchema } : undefined,
      });

      const targets = (tRes?.data ?? []) as any[];
      const options: RefOption[] = targets.map((r: any) => ({
        value: r[targetIdField],
        label:
          r[optionText] != null
            ? String(r[optionText])
            : String(r[targetIdField]),
      }));
      setAllTargets(options);

      // 3) Merge for display
      const optById = new Map(options.map((o) => [o.value, o.label]));
      const mapped = junctionRows.map((jr: any) => {
        const rid = jr[rightKey];
        return {
          ...jr,
          _label: optById.get(rid) ?? String(rid),
          _rightId: rid,
        };
      });

      setLinks(mapped);
      setToAdd(undefined);
    } catch (e: any) {
      console.error("[LinkTableEditor] load failed", e);
      notify({
        type: "error",
        message: "Failed to load members",
        description: e?.message || e?.response?.data?.message,
      });
    } finally {
      setLoadingLinks(false);
    }
  }, [
    canUse,
    dataProvider,
    junctionResource,
    junctionSchema,
    leftKey,
    leftId,
    targetResource,
    targetSchema,
    targetIdField,
    optionText,
    rightKey,
    notify,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const addLink = async () => {
    if (!canUse || toAdd == null) return;
    setAdding(true);
    try {
      // Defaults for extra fields
      const defaults: any = {};
      extraFields.forEach((ef: any) => {
        if (ef.defaultValue !== undefined)
          defaults[ef.source] = ef.defaultValue;
      });

      await dataProvider.create({
        resource: junctionResource,
        variables: {
          [leftKey]: leftId,
          [rightKey]: toAdd,
          ...defaults,
        },
        meta: junctionSchema ? { schema: junctionSchema } : undefined,
      });

      notify({ type: "success", message: "Member added" });
      await load();
    } catch (e: any) {
      console.error("[LinkTableEditor] add failed", e);
      notify({
        type: "error",
        message: "Failed to add member",
        description: e?.message || e?.response?.data?.message,
      });
    } finally {
      setAdding(false);
    }
  };

  const removeLink = async (junctionRowId: string) => {
    if (!canUse) return;
    setRemovingId(junctionRowId);
    try {
      await dataProvider.deleteOne({
        resource: junctionResource,
        id: junctionRowId,
        meta: junctionSchema ? { schema: junctionSchema } : undefined,
      });

      notify({ type: "success", message: "Member removed" });
      await load();
    } catch (e: any) {
      console.error("[LinkTableEditor] remove failed", e);
      notify({
        type: "error",
        message: "Failed to remove member",
        description: e?.message || e?.response?.data?.message,
      });
    } finally {
      setRemovingId(null);
    }
  };

  // Update permission field on junction record
  const updateLinkField = async (
    rowId: string,
    fieldName: string,
    newValue: any,
  ) => {
    const idx = links.findIndex((l) => l.id === rowId);
    if (idx === -1) return;

    const oldItem = links[idx];
    const newItem = { ...oldItem, [fieldName]: newValue };
    setLinks(updateItem(links, idx, newItem));

    try {
      await dataProvider.update({
        resource: junctionResource,
        id: rowId,
        variables: { [fieldName]: newValue },
        meta: junctionSchema ? { schema: junctionSchema } : undefined,
      });
    } catch (e: any) {
      setLinks(updateItem(links, idx, oldItem));
      console.error("[LinkTableEditor] update failed", e);
      notify({
        type: "error",
        message: "Failed to update permissions",
        description: e?.message || e?.response?.data?.message,
      });
    }
  };

  const existingRightIds = useMemo(
    () => new Set(links.map((l) => l._rightId)),
    [links],
  );

  const addOptions = useMemo(
    () => allTargets.filter((o) => !existingRightIds.has(o.value)),
    [allTargets, existingRightIds],
  );

  return (
    <div
      style={{
        padding: 12,
        border: `1px solid ${colorBorderSecondary}`,
        borderRadius: 8,
        background: colorBgContainer,
        color: colorText,
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <Text strong style={{ color: colorText }}>
          {label}
        </Text>
        {!leftId && (
          <Text type="secondary" style={{ color: colorTextSecondary }}>
            Save record first
          </Text>
        )}
      </div>

      {/* List of Linked Items */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          marginBottom: 12,
        }}
      >
        {loadingLinks && links.length === 0 ? (
          <Text type="secondary" style={{ color: colorTextSecondary }}>
            Loading...
          </Text>
        ) : (
          links.map((l) => (
            <div
              key={l.id}
              style={{
                display: "grid",
                gridTemplateColumns: isXS
                  ? "1fr"
                  : "minmax(160px, 1fr) auto auto",
                alignItems: "start",
                gap: 12,
                padding: "10px 12px",
                border: `1px solid ${colorBorderSecondary}`,
                borderRadius: 8,
                background: colorBgElevated,
                overflow: "hidden",
                maxWidth: "100%",
              }}
            >
              {/* left: label */}
              <div style={{ minWidth: 0 }}>
                <Text
                  strong
                  style={{ color: colorText, wordBreak: "break-word" }}
                >
                  {l._label}
                </Text>
              </div>

              {/* middle: permissions */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                  justifyContent: isXS ? "flex-start" : "flex-end",
                  maxWidth: "100%",
                }}
              >
                {extraFields.map((ef: any) =>
                  ef.kind === "boolean" ? (
                    <Checkbox
                      key={ef.source}
                      checked={!!l[ef.source]}
                      onChange={(e) =>
                        updateLinkField(l.id, ef.source, e.target.checked)
                      }
                    >
                      <span style={{ color: colorText }}>
                        {ef.label || ef.source}
                      </span>
                    </Checkbox>
                  ) : null,
                )}
              </div>

              {/* right: delete */}
              <div style={{ justifySelf: isXS ? "start" : "end" }}>
                <Button
                  size="small"
                  type="text"
                  danger
                  loading={removingId === l.id}
                  onClick={() => removeLink(l.id)}
                  icon={<DeleteOutlined />}
                />
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Control */}
      <Flex gap={8}>
        <Select
          style={{ flex: 1 }}
          placeholder={`Add ${label.slice(0, -1) || "item"}...`}
          disabled={!canUse || adding}
          showSearch
          optionFilterProp="label"
          options={addOptions}
          value={toAdd}
          onChange={setToAdd}
        />
        <Button
          type="primary"
          onClick={addLink}
          disabled={!canUse || toAdd == null}
          loading={adding}
          icon={<PlusOutlined />}
        >
          Add
        </Button>
      </Flex>
    </div>
  );
}

export const GenericCrud: React.FC<GenericCrudProps> = ({
  resource,
  mobile,
}) => {
  const refreshTimerRef = useRef<number | null>(null);
/*
  const {
    token: { colorText, colorBorderSecondary, colorBgContainer },
  } = antdTheme.useToken();
jek
*/
const { token } = antdTheme.useToken();
const { colorText, colorBorderSecondary, colorBgContainer } = token;


  const cfg: ResourceConfig | undefined = useMemo(
    () => RESOURCES.find((r) => r.name === resource),
    [resource],
  );

  const dataProviderFromHook = useDataProvider();
  const dataProvider = useMemo(
    () =>
      typeof dataProviderFromHook === "function"
        ? dataProviderFromHook()
        : (dataProviderFromHook as any),
    [dataProviderFromHook],
  );

  const { open: notify } = useNotification();
  const screens = useBreakpoint();
  const isMobile = mobile ?? !!screens.xs;

  const drawerWidth = useMemo(() => {
    if (screens.xs) return "100%";
    if (screens.sm) return 560;
    if (screens.md) return 720;
    return 960;
  }, [screens]);

  const showNotification = useCallback(
    (args: {
      type?: "success" | "error" | "warning" | "info";
      message: string;
      description?: string;
    }) => {
      if (typeof notify === "function") notify(args);
    },
    [notify],
  );

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [search, setSearch] = useState("");

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  const [refOptions, setRefOptions] = useState<Record<string, RefOption[]>>({});

  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [outputDrawerOpen, setOutputDrawerOpen] = useState(false);
  const [outputRecord, setOutputRecord] = useState<any | null>(null);

  const [actionPromptState, setActionPromptState] = useState<ActionPromptState>(
    {
      open: false,
      action: null,
      record: null,
      args: [],
      options: {},
    },
  );
  const [actionForm] = Form.useForm();

  const listReqRef = useRef(0);
  const refReqRef = useRef(0);

  // Focus helper refs
  const topAnchorRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!modalOpen && UI.REFOCUS_TOP_ON_CLOSE) {
      setTimeout(() => {
        containerRef.current?.focus({ preventScroll: false });
      }, 100);
    }
  }, [modalOpen]);

  useEffect(() => {
    if (modalOpen && topAnchorRef.current) {
      setTimeout(() => {
        topAnchorRef.current?.focus({ preventScroll: false });
      }, 50);
    }
  }, [modalOpen]);

  if (!cfg) {
    return (
      <Result
        status="error"
        title="Unknown resource"
        subTitle={`No configuration found for resource "${resource}".`}
      />
    );
  }

  const listFields = cfg.fields.filter((f) => f.list);
  const formFields = cfg.fields.filter((f) => f.form !== false);

  const refFields = useMemo(
    () => cfg.fields.filter((f) => (f as any).ref),
    [cfg],
  );

  const allActions = cfg.actions || [];
  const toolbarActions = allActions.filter((a) => (a as any).toolbar);
  const toolbarMode: "buttons" | "select" =
    (cfg as any).toolbarMode ||
    ((cfg as any).toolbarAsSelect ? "select" : "buttons");

  const rowActions = allActions.filter(
    (a) => (a as any).row ?? !(a as any).toolbar,
  );

  const actionNeedsSelection = (a: any) =>
    !!a?.useSelectedIdsAs || !!a?.batchHosts;

  const canRunToolbarAction = (a: any) => {
    if (actionNeedsSelection(a)) return selectedRowKeys.length > 0;
    return true;
  };

  const toolbarMenuItems = useMemo(() => {
    return toolbarActions.map((a, idx) => {
      const needsSel =
        !!(a as any)?.useSelectedIdsAs || !!(a as any)?.batchHosts;
      const enabled = !needsSel || selectedRowKeys.length > 0;
      const icon =
        a.kind === "download" ? "⬇️" : a.kind === "rpc" ? "⚡" : "▶️";

      return {
        key: `ta-${idx}`,
        disabled: !enabled,
        label: (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div>
              <span style={{ marginRight: 6 }}>{icon}</span>
              {a.label}
            </div>
            {needsSel && !enabled && (
              <div style={{ fontSize: 11, opacity: 0.65 }}>
                Select one or more rows first
              </div>
            )}
          </div>
        ),
      };
    });
  }, [toolbarActions, selectedRowKeys.length]);

  const [sorters, setSorters] = useState<any[]>(() => {
    const ds = (cfg as any)?.defaultSort;
    if (!ds) return [];
    return Array.isArray(ds) ? ds : [ds];
  });

  useEffect(() => {
    const ds = (cfg as any)?.defaultSort;
    setSorters(ds ? (Array.isArray(ds) ? ds : [ds]) : []);
    setPagination((prev) => ({ ...prev, current: 1 }));
    setSelectedRowKeys([]);
    setSelectedRows([]);
    form.resetFields();
  }, [resource, cfg, form]);

  const buildFilters = useCallback(() => {
    const q = search.trim();
    if (!q) return [];
    return [{ field: "search", operator: "contains", value: q }];
  }, [search]);

  const fetchTable = useCallback(
    async ({
      current,
      pageSize,
      sorters: nextSorters,
    }: {
      current: number;
      pageSize: number;
      sorters: any[];
    }) => {
      if (!dataProvider?.getList) return;
      listReqRef.current += 1;
      const reqId = listReqRef.current;
      setLoading(true);
      try {
        const filters = buildFilters();
        const res: any = await dataProvider.getList({
          resource,
          pagination: { current, pageSize },
          filters,
          sorters: nextSorters,
          meta: cfg.schema ? { schema: cfg.schema } : undefined,
        });
        if (reqId !== listReqRef.current) return;
        setRows(res?.data ?? []);
        setPagination((prev) => ({
          ...prev,
          current,
          pageSize,
          total: res?.total ?? prev.total,
        }));
      } catch (error: any) {
        if (reqId !== listReqRef.current) return;
        console.error(error);
        showNotification({ type: "error", message: "Failed to load data" });
      } finally {
        if (reqId === listReqRef.current) setLoading(false);
      }
    },
    [buildFilters, cfg.schema, dataProvider, resource, showNotification],
  );

  const triggerDebouncedRefresh = useCallback(() => {
    if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = window.setTimeout(() => {
      fetchTable({
        current: pagination.current,
        pageSize: pagination.pageSize,
        sorters,
      });
    }, 250);
  }, [fetchTable, pagination.current, pagination.pageSize, sorters]);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
    };
  }, []);

  // --- pgbroadcast realtime subscription (refcounted singleton) ---
  const topic = useMemo(() => {
    const schema = cfg?.schema || "public";
    const table = (cfg as any)?.tableName || resource; // optional override
    return `${schema}.${table}`;
  }, [cfg?.schema, (cfg as any)?.tableName, resource]);

  usePGBroadcastRefresh({
    topics: [topic],
    onChange: triggerDebouncedRefresh,
    enabled: true,
    debounceMs: 250,
  });

  useEffect(() => {
    const t = window.setTimeout(() => {
      fetchTable({
        current: pagination.current,
        pageSize: pagination.pageSize,
        sorters,
      });
    }, 250);
    return () => window.clearTimeout(t);
  }, [fetchTable, pagination.current, pagination.pageSize, sorters, search]);

  useEffect(() => {
    const loadRefOptions = async () => {
      if (!dataProvider?.getList || !refFields.length) {
        setRefOptions({});
        return;
      }
      refReqRef.current += 1;
      const reqId = refReqRef.current;
      const optionsMap: Record<string, RefOption[]> = {};
      for (const field of refFields) {
        const ref = (field as any).ref;
        if (!ref?.resource) continue;
        try {
          const res: any = await dataProvider.getList({
            resource: ref.resource,
            pagination: { current: 1, pageSize: 1000 },
            meta: ref.schema ? { schema: ref.schema } : undefined,
          });
          if (reqId !== refReqRef.current) return;
          const optText = ref.optionText || "name";
          const options = (res?.data ?? []).map((row: any) => ({
            value: row.id,
            label: row[optText] != null ? String(row[optText]) : String(row.id),
          }));
          optionsMap[field.source] = options;
          const valueFrom = (field as any).valueFrom;
          if (valueFrom && !optionsMap[valueFrom]) {
            optionsMap[valueFrom] = options;
          }
        } catch {}
      }
      if (reqId !== refReqRef.current) return;
      setRefOptions(optionsMap);
    };
    loadRefOptions();
  }, [dataProvider, resource, refFields]);

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    const initialValues: any = {};
    formFields.forEach((field) => {
      const def = (field as any).defaultValue;
      if (def !== undefined) initialValues[field.source] = def;
    });
    form.setFieldsValue(initialValues);
    setModalOpen(true);
  };

  const openEdit = (record: any) => {
    setEditingId(record.id);
    const initialValues: any = { ...record };
    formFields.forEach((field) => {
      const key = field.source;
      const raw = record[key];
      if (isDateField(field)) initialValues[key] = raw ? dayjs(raw) : null;
      if (isBooleanField(field) && (raw === 0 || raw === 1))
        initialValues[key] = !!raw;
    });
    form.setFieldsValue(initialValues);
    setModalOpen(true);
  };

  const handleDelete = async (record: any) => {
    try {
      await dataProvider.deleteOne({
        resource,
        id: record.id,
        meta: cfg.schema ? { schema: cfg.schema } : undefined,
      });
      showNotification({ type: "success", message: "Deleted successfully" });
      fetchTable({
        current: pagination.current,
        pageSize: pagination.pageSize,
        sorters,
      });
    } catch {
      showNotification({ type: "error", message: "Delete failed" });
    }
  };

  const handleFormFinish = async (values: any) => {
    try {
      const normalized = normalizeValuesWithFields(values, formFields);
      if (editingId) {
        await dataProvider.update({
          resource,
          id: editingId,
          variables: normalized,
          meta: cfg.schema ? { schema: cfg.schema } : undefined,
        });
      } else {
        await dataProvider.create({
          resource,
          variables: normalized,
          meta: cfg.schema ? { schema: cfg.schema } : undefined,
        });
      }
      showNotification({ type: "success", message: "Saved successfully" });
      setModalOpen(false);
      setEditingId(null);
      fetchTable({
        current: pagination.current,
        pageSize: pagination.pageSize,
        sorters,
      });
    } catch {
      showNotification({ type: "error", message: "Save failed" });
    }
  };

  const handleTableChange = (pageInfo: any, _filters: any, sorter: any) => {
    let nextSorters: any[] = [];
    if (Array.isArray(sorter)) {
      nextSorters = sorter
        .filter((s) => s.field && s.order)
        .map((s) => ({
          field: s.field,
          order: s.order === "descend" ? "desc" : "asc",
        }));
    } else if (sorter?.field) {
      nextSorters = [
        {
          field: sorter.field,
          order: sorter.order === "descend" ? "desc" : "asc",
        },
      ];
    }
    setSorters(nextSorters);
    setPagination((prev) => ({
      ...prev,
      current: pageInfo.current || 1,
      pageSize: pageInfo.pageSize || prev.pageSize,
    }));
    fetchTable({
      current: pageInfo.current || 1,
      pageSize: pageInfo.pageSize || pagination.pageSize,
      sorters: nextSorters,
    });
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const renderFormField = (field: ResourceField) => {
    const rules = (field as any).required ? [{ required: true }] : [];
    const disabled = (field as any).editDisabled && editingId != null;
    const isDisplayOnly =
      !!(field as any).displayOnly || !!(field as any).readOnly;

    if (isLinkTableField(field)) {
      return (
        <div key={field.source} style={{ marginBottom: 16 }}>
          <LinkTableEditor
            field={{ ...field, _recordId: editingId } as any}
            dataProvider={dataProvider}
            notify={showNotification}
          />
        </div>
      );
    }

    if (isDisplayOnly) {
      return (
        <DisplayOnlyField
          key={field.source}
          field={field}
          form={form}
          refOptions={refOptions}
        />
      );
    }

    if ((field as any).ref) {
      const options = refOptions[field.source] || [];
      const inputType = (field as any).ref.input || "select";
      return (
        <Form.Item
          key={field.source}
          label={field.label || field.source}
          name={field.source}
          rules={rules}
        >
          <Select
            disabled={disabled}
            showSearch={inputType === "autocomplete"}
            optionFilterProp="label"
            options={options}
            allowClear
            style={{ width: "100%" }}
          />
        </Form.Item>
      );
    }

    if (isSelectField(field)) {
      return (
        <Form.Item
          key={field.source}
          label={field.label || field.source}
          name={field.source}
          rules={rules}
        >
          <Select
            disabled={disabled}
            options={(field as any).choices}
            allowClear
            style={{ width: "100%" }}
          />
        </Form.Item>
      );
    }

    if (isMarkdownField(field)) {
      return (
        <div key={field.source}>
          <MarkdownField
            resourceName={resource}
            form={form}
            field={field}
            rules={rules}
            disabled={disabled}
          />
        </div>
      );
    }

    let node: React.ReactNode = <Input disabled={disabled} />;
    if (isNumberField(field))
      node = <InputNumber disabled={disabled} style={{ width: "100%" }} />;
    else if (isBooleanField(field)) node = <Checkbox disabled={disabled} />;
    else if (isDateField(field))
      node = (
        <DatePicker showTime style={{ width: "100%" }} disabled={disabled} />
      );
    else if (isLongTextField(field))
      node = <TextArea rows={4} disabled={disabled} />;

    return (
      <Form.Item
        key={field.source}
        label={field.label || field.source}
        name={field.source}
        rules={rules}
        valuePropName={isBooleanField(field) ? "checked" : "value"}
      >
        {node}
      </Form.Item>
    );
  };

  const formContent = (
    <Form
      layout="vertical"
      form={form}
      onFinish={handleFormFinish}
      initialValues={{}}
    >
      <TopFocusAnchor />
      {/* Top Aligned Action Buttons */}
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
          Save
        </Button>
        <Button onClick={() => setModalOpen(false)}>Cancel</Button>
      </Space>

      {formFields.map((f) => renderFormField(f))}
    </Form>
  );

  // --- UNIFIED EXECUTION LOGIC (RPC / Download / Batch) ---
  const executeResourceAction = async (
    action: ResourceAction,
    values: any,
    record: any,
  ) => {
    try {
      const actionSchema = (action as any).schema || cfg.schema || undefined;
      const apiUrl =
        typeof (dataProvider as any).getApiUrl === "function"
          ? (dataProvider as any).getApiUrl().replace(/\/$/, "")
          : "";
      const url = `${apiUrl}/rpc/${(action as any).rpcName}`;

      let payload: any = {};

      // 1. Base payload from record
      if ((action as any).sendRecord && !(action as any).useIdAs)
        payload = { ...(record || {}) };
      if ((action as any).useIdAs && record?.id)
        payload[(action as any).useIdAs] = record.id;
      if ((action as any).payload)
        payload = { ...payload, ...(action as any).payload };

      // 2. Handle batch selection logic
      const selectedField = (action as any).useSelectedIdsAs;
      if (selectedField) {
        if (!selectedRowKeys.length) {
          showNotification({
            type: "warning",
            message: "Please select one or more rows first",
          });
          return;
        }
        payload[selectedField] = selectedRowKeys;
      }

      // 3. Process Prompt Values (Text Array handling)
      const processedValues = { ...values };
      const promptArgs = (action as any).promptArgs as
        | PromptArgConfig[]
        | undefined;

      if (promptArgs) {
        for (const arg of promptArgs) {
          if (arg.asTextArray) {
            const raw = processedValues[arg.name];
            if (raw == null || String(raw).trim() === "") {
              delete processedValues[arg.name];
              continue;
            }
            const trimmed = String(raw).trim();
            const parts = trimmed
              .split(",")
              .map((s: string) => s.trim())
              .filter(Boolean)
              .map((s: string) => `"${s.replace(/"/g, '\\"')}"`);
            processedValues[arg.name] = `{${parts.join(",")}}`;
          }
        }
      }

      // 4. Merge prompt/batch values ON TOP of base payload
      payload = { ...payload, ...processedValues };

      // === EXECUTE ===
      const res: any = await dataProvider.custom({
        url,
        method: (action as any).method || "POST",
        data: payload,
        resource,
        meta: actionSchema ? { schema: actionSchema } : undefined,
      });

      // === HANDLE RESPONSE ===
      if (action.kind === "download") {
        const filenameTemplate = (action as any).filename || "download.bin";
        const filename = filenameTemplate.replace(
          /\{(\w+)\}/g,
          (_: any, key: any) => String(record?.[key] ?? key),
        );

        const data = res?.data;
        let blob: Blob;

        const fromPgHex = (val: string): Uint8Array => {
          const s = val.startsWith("\\x") ? val.slice(2) : val;
          const bytes = new Uint8Array(s.length / 2);
          for (let i = 0; i < s.length; i += 2)
            bytes[i / 2] = parseInt(s.slice(i, i + 2), 16);
          return bytes;
        };
        const isPgHex = (val: unknown): val is string =>
          typeof val === "string" && val.startsWith("\\x");

        const pickVal = (r: any) => {
          const explicit = (action as any).resultField;
          return (
            (explicit ? r?.[explicit] : undefined) ??
            r?.data ??
            r?.content ??
            r?.result ??
            r?.file ??
            r?.[Object.keys(r || {})[0]]
          );
        };

        if (data instanceof Blob) {
          blob = data;
        } else if (Array.isArray(data) && data.length > 0) {
          const val = pickVal(data[0]);
          if (isPgHex(val))
            blob = new Blob([fromPgHex(val)], {
              type: (action as any).mimeType || "application/octet-stream",
            });
          else
            blob = new Blob([val], {
              type: (action as any).mimeType || "text/plain",
            });
        } else if (data && typeof data === "object") {
          const val = pickVal(data);
          if (isPgHex(val))
            blob = new Blob([fromPgHex(val)], {
              type: (action as any).mimeType || "application/octet-stream",
            });
          else
            blob = new Blob([val], {
              type: (action as any).mimeType || "text/plain",
            });
        } else if (typeof data === "string") {
          if (isPgHex(data))
            blob = new Blob([fromPgHex(data)], {
              type: (action as any).mimeType || "application/octet-stream",
            });
          else
            blob = new Blob([data], {
              type: (action as any).mimeType || "text/plain",
            });
        } else {
          throw new Error("Unexpected download response data");
        }

        downloadFile(blob, filename);
        showNotification({
          type: "success",
          message: `${action.label} started`,
        });
      } else {
        if ((action as any).copyToClipboard) {
          const body: any = res?.data;
          const text =
            typeof body === "string"
              ? body
              : body && typeof body === "object"
                ? JSON.stringify(body, null, 2)
                : String(body);
          await navigator.clipboard.writeText(text);
          showNotification({
            type: "success",
            message: `${action.label} copied`,
          });
        } else {
          let batchId: string | undefined;
          const key = (action as any).rpcName;
          if (Array.isArray(res?.data) && res.data.length > 0)
            batchId = res.data[0]?.[key];
          showNotification({
            type: "success",
            message: `${action.label} succeeded`,
            description: batchId ? `Batch ID: ${batchId}` : undefined,
          });
        }
      }

      if ((action as any).refreshAfter) {
        await fetchTable({
          current: pagination.current,
          pageSize: pagination.pageSize,
          sorters,
        });
      }

      // Cleanup
      if ((action as any).batchHosts || actionNeedsSelection(action)) {
        setSelectedRowKeys([]);
        setSelectedRows([]);
      }
    } catch (error: any) {
      console.error("[GenericCrud] action failed", error);
      showNotification({
        type: "error",
        message: error?.message || "Action failed",
        description: error?.body?.details || error?.body?.hint || undefined,
      });
    }
  };

  const runAction = async (action: ResourceAction, record?: any) => {
    // 1. Copy/View Output/Simple types
    if (action.kind === "copyField") {
      const raw = record?.[(action as any).source];
      const text =
        raw == null ? "" : Array.isArray(raw) ? raw.join("\n") : String(raw);
      if (!text) {
        showNotification({ type: "warning", message: "Nothing to copy" });
        return;
      }
      await navigator.clipboard.writeText(text);
      showNotification({ type: "success", message: `${action.label} copied` });
      return;
    }

    if (action.kind === "downloadField") {
      const raw = record?.[(action as any).source];
      const text =
        raw == null ? "" : Array.isArray(raw) ? raw.join("\n") : String(raw);
      const blob = new Blob([text], {
        type: (action as any).mimeType || "application/octet-stream",
      });
      const filename = ((action as any).filename || "download.txt").replace(
        /\{(\w+)\}/g,
        (_: any, key: any) => String(record?.[key] ?? key),
      );
      downloadFile(blob, filename);
      showNotification({ type: "success", message: `${action.label} started` });
      return;
    }

    if (action.kind === "viewOutput") {
      if (!record) {
        showNotification({ type: "warning", message: "No row selected" });
        return;
      }
      setOutputRecord(record);
      setOutputDrawerOpen(true);
      return;
    }

    // 2. Check for Batch Hosts Special Case (uses native prompt)
    if ((action as any).batchHosts) {
      if (!selectedRows.length) {
        showNotification({ type: "warning", message: "No hosts selected" });
        return;
      }
      const cmd =
        window.prompt(
          `Shell command to run on ${selectedRows.length} host(s):`,
        ) || "";
      if (!cmd.trim()) {
        showNotification({ type: "info", message: "Action cancelled" });
        return;
      }

      executeResourceAction(
        action,
        {
          p_command: cmd.trim(),
          p_host_ids: selectedRowKeys,
        },
        record || { id: null },
      );
      return;
    }

    // 3. Setup Args for Modal (PromptArgs + Password)
    const promptArgs = ((action as any).promptArgs as PromptArgConfig[]) || [];
    const effectiveArgs = [...promptArgs];

    if ((action as any).promptPassword) {
      effectiveArgs.push({
        name:
          (action as any).passwordField ||
          (action.kind === "download" ? "p_output_password" : "p_password"),
        label: "Password (leave blank to clear)",
        kind: "password",
        optional: true,
      });
    }

    // 4. If no args needed, execute immediately
    if (effectiveArgs.length === 0) {
      executeResourceAction(action, {}, record);
      return;
    }

    // 5. Otherwise, open Modal
    const optionsMap: Record<string, RefOption[]> = {};
    for (const arg of effectiveArgs) {
      if (arg.kind === "select" && arg.ref?.resource) {
        try {
          const res: any = await dataProvider.getList({
            resource: arg.ref.resource,
            pagination: { current: 1, pageSize: 1000 },
          });
          const optText = arg.ref.optionText || "name";
          optionsMap[arg.name] = (res?.data ?? []).map((row: any) => ({
            value: row.id,
            label: row[optText] != null ? String(row[optText]) : String(row.id),
          }));
        } catch {}
      }
    }

    // Set defaults
    const initialValues: any = {};
    for (const arg of effectiveArgs) {
      if (arg.defaultValue !== undefined)
        initialValues[arg.name] = arg.defaultValue;
    }
    actionForm.setFieldsValue(initialValues);

    setActionPromptState({
      open: true,
      action,
      record: record || null,
      args: effectiveArgs,
      options: optionsMap,
    });
  };

  // ---- Row action menu builder + cell ----

  const canEdit = !cfg.readOnly && !(cfg as any).hideEdit;
  const canDelete = !cfg.readOnly && !(cfg as any).hideDelete;

  const getRowMenuItems = useCallback(
    (record: any) => {
      const ctx: RowActionContext = { record, selectedRowKeys };

      const dynamicActions = rowActions.filter((a) =>
        isRowActionVisible(a, ctx),
      );

      const items: any[] = [];

      if (canEdit)
        items.push({ key: "edit", icon: <EditOutlined />, label: "Edit" });
      if (canDelete)
        items.push({
          key: "delete",
          icon: <DeleteOutlined style={{ color: "red" }} />,
          label: <span style={{ color: "red" }}>Delete</span>,
        });

      dynamicActions.forEach((a, idx) => {
        items.push({
          key: `ra-${idx}`,
          label: a.label || (a as any).rpcName || a.kind || "Action",
        });
      });

      return { items, dynamicActions };
    },
    [rowActions, selectedRowKeys, canEdit, canDelete],
  );

  const RowActionsCell: React.FC<{ record: any }> = ({ record }) => {
    const { items, dynamicActions } = getRowMenuItems(record);

    if (!items.length) return null;

    return (
      <Dropdown
        trigger={["click"]}
        placement="bottomLeft"
        menu={{
          items,
          onClick: async ({ key }) => {
            if (key === "edit") return openEdit(record);
            if (key === "delete") return handleDelete(record);
            if (String(key).startsWith("ra-")) {
              const idx = Number(String(key).slice(3));
              const action = dynamicActions[idx];
              if (!action) return;
              await runAction(action, record);
            }
          },
        }}
      >
        <Button
          type="text"
          size="small"
          icon={<MoreOutlined />}
          onClick={(e) => e.stopPropagation()}
        />
      </Dropdown>
    );
  };

  // ---- Columns (IMPORTANT: use columns=... prop, not <Table.Column/>) ----

  const listColumns = useMemo(() => {
    return listFields.map((field) => ({
      key: field.source,
      dataIndex: field.source,
      title: field.label || field.source,
      sorter: true,
      render: (value: any, record: any) => {
        let display: React.ReactNode;
        const isDisplayOnly =
          !!(field as any).displayOnly || !!(field as any).readOnly;

        if (isDisplayOnly && (field as any).valueFrom) {
          const idKey = (field as any).valueFrom as string;
          const idVal = record?.[idKey];
          const ref = (field as any).ref;
          if (ref && idVal != null) {
            const opts = refOptions[field.source] || [];
            const opt = opts.find((o) => o.value === idVal);
            display = opt ? opt.label : String(idVal);
          } else {
            display = idVal != null ? String(idVal) : "";
          }
        } else if (isSelectField(field) && (field as any).choices) {
          const opt = (field as any).choices.find(
            (o: any) => o.value === value,
          );
          display = opt ? opt.label : value;
        } else if ((field as any).ref && refOptions[field.source]) {
          const opt = refOptions[field.source].find((o) => o.value === value);
          display = opt ? opt.label : value;
        } else if (isMarkdownField(field)) {
          const text = value != null ? String(value) : "";
          const previewLines = (field as any).previewLines ?? 3;
          const preview = text.split("\n").slice(0, previewLines).join("\n");
          display = (
            <div style={{ maxWidth: 700 }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {preview || ""}
              </ReactMarkdown>
              {text.split("\n").length > previewLines && (
                <div style={{ fontSize: 11, opacity: 0.6 }}>…</div>
              )}
            </div>
          );
        } else {
          display = renderFieldValue(field, value);
        }

        const colored = renderValueBadge(token, field, value, display);

        return (
          <div
            style={{
              maxHeight: 200,
              maxWidth: 600,
              overflow: "auto",
              whiteSpace: "pre-wrap",
              fontSize: 13,
            }}
          >
            {colored}
          </div>
        );
      },
    }));
  }, [listFields, refOptions]);

  // Show the ellipsis column only if there is *any* possible menu item (Edit/Delete/row actions)
  const hasAnyRowMenu = canEdit || canDelete || (rowActions?.length ?? 0) > 0;

  const actionCol = hasAnyRowMenu
    ? ({
        key: "__row_actions__",
        dataIndex: "__row_actions__",
        title: "",
        width: 44,
        align: "center" as const,
        render: (_: any, record: any) => <RowActionsCell record={record} />,
        onCell: (record: any) => {
          const { items } = getRowMenuItems(record);
          if (!items.length) {
            return {
              style: {
                padding: 0,
                width: 0,
                minWidth: 0,
                maxWidth: 0,
              } as React.CSSProperties,
            };
          }
          return {
            style: { paddingLeft: 4, paddingRight: 4 } as React.CSSProperties,
          };
        },
      } as const)
    : null;

  const columns = useMemo(() => {
    const cols: any[] = [];
    if (actionCol) cols.push(actionCol);
    cols.push(...listColumns);
    return cols;
  }, [actionCol, listColumns]);

  const rowSelectionConfig = (cfg as any).enableSelection
    ? {
        selectedRowKeys,
        onChange: (keys: React.Key[], selected: any[]) => {
          setSelectedRowKeys(keys);
          setSelectedRows(selected);
        },
      }
    : undefined;

  return (
    <div ref={containerRef} tabIndex={-1} style={{ outline: "none" }}>
      {/* HEADER CONTROLS */}
      <Flex wrap gap={12} style={{ marginBottom: 16 }}>
        {/* Search + Refresh Group */}
        <Space.Compact>
          <Input
            placeholder={`Search ${cfg.label || resource}`}
            allowClear
            value={search}
            onChange={onSearchChange}
            style={{ width: 220 }}
            suffix={
              search ? (
                <CloseCircleOutlined
                  onClick={() => setSearch("")}
                  style={{ cursor: "pointer" }}
                />
              ) : null
            }
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchTable({
                current: 1,
                pageSize: pagination.pageSize,
                sorters,
              });
            }}
          />
        </Space.Compact>

        {/* New Button */}
        {!cfg.readOnly && !(cfg as any).hideCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New
          </Button>
        )}

        {/* Action Dropdown (if multiple/toolbarMode is select) */}
        {toolbarActions.length > 0 && toolbarMode === "select" ? (
          <Dropdown
            trigger={["click"]}
            placement="bottomLeft"
            menu={{
              items: toolbarMenuItems,
              onClick: ({ key }) => {
                if (!String(key).startsWith("ta-")) return;
                const idx = Number(String(key).slice(3));
                const action = toolbarActions[idx];
                if (!action) return;
                if (!canRunToolbarAction(action)) {
                  showNotification({
                    type: "warning",
                    message: "Select one or more rows first",
                  });
                  return;
                }
                runAction(action);
              },
            }}
          >
            <Button>
              Actions <MoreOutlined />
            </Button>
          </Dropdown>
        ) : (
          <Space>
            {toolbarActions.map((a, idx) => {
              const disabled = !canRunToolbarAction(a);
              return (
                <Button
                  key={`${a.label}-${idx}`}
                  onClick={() => {
                    if (disabled) {
                      showNotification({
                        type: "warning",
                        message: "Select rows first",
                      });
                      return;
                    }
                    runAction(a);
                  }}
                  disabled={disabled}
                >
                  {a.label}
                </Button>
              );
            })}
          </Space>
        )}
      </Flex>

      <Table
        columns={columns}
        dataSource={rows}
        loading={loading}
        rowKey="id"
        size="small"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50", "100", "200"],
        }}
        onChange={handleTableChange}
        rowSelection={rowSelectionConfig}
        scroll={isMobile ? undefined : { x: 1000 }}
      />

      {isMobile ? (
        <Drawer
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title={editingId ? "Edit" : "Create"}
          width="100%"
          placement="left"
          destroyOnClose
        >
          {formContent}
        </Drawer>
      ) : (
        <Modal
          width={drawerWidth}
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          title={editingId ? "Edit" : "Create"}
          footer={null}
          destroyOnHidden={false}
          forceRender
          style={{ top: 20 }}
        >
          {formContent}
        </Modal>
      )}

      {/* Action Prompt Modal */}
      <Modal
        open={actionPromptState.open}
        title={actionPromptState.action?.label || "Action"}
        onCancel={() => {
          setActionPromptState({
            open: false,
            action: null,
            record: null,
            args: [],
            options: {},
          });
          actionForm.resetFields();
        }}
        onOk={actionForm.submit}
        destroyOnHidden
      >
        <Form
          layout="vertical"
          form={actionForm}
          onFinish={async (vals) => {
            const a = actionPromptState.action;
            if (!a) return;
            await executeResourceAction(a, vals, actionPromptState.record);
            setActionPromptState({
              open: false,
              action: null,
              record: null,
              args: [],
              options: {},
            });
            actionForm.resetFields();
          }}
        >
          {actionPromptState.args.map((arg) => {
            const required = !arg.optional;
            const itemRules = required ? [{ required: true }] : [];

            if (arg.kind === "select") {
              const opts = actionPromptState.options[arg.name] || [];
              return (
                <Form.Item
                  key={arg.name}
                  label={arg.label}
                  name={arg.name}
                  rules={itemRules}
                >
                  <Select
                    options={opts}
                    showSearch
                    optionFilterProp="label"
                    allowClear={arg.optional}
                  />
                </Form.Item>
              );
            }

            if (arg.kind === "password") {
              return (
                <Form.Item
                  key={arg.name}
                  label={arg.label}
                  name={arg.name}
                  rules={itemRules}
                >
                  <Input.Password />
                </Form.Item>
              );
            }

            return (
              <Form.Item
                key={arg.name}
                label={arg.label}
                name={arg.name}
                rules={itemRules}
              >
                <Input />
              </Form.Item>
            );
          })}
        </Form>
      </Modal>

      {/* OUTPUT DRAWER */}
      <Drawer
        open={outputDrawerOpen}
        onClose={() => {
          setOutputDrawerOpen(false);
          setOutputRecord(null);
        }}
        title={
          outputRecord
            ? `Command Output ${outputRecord.host_id || ""}`
            : "Command Output"
        }
        width={720}
      >
        {outputRecord ? (
          <div style={{ fontFamily: "monospace", fontSize: 12 }}>
            <p>
              <strong>Host ID:</strong> {outputRecord.host_id || "(unknown)"}
            </p>
            <p>
              <strong>Batch ID:</strong> {outputRecord.batch_id || "(none)"}
            </p>
            <p>
              <strong>Status ID:</strong>{" "}
              {outputRecord.status_id ?? "(unknown)"}
            </p>
            <p>
              <strong>Exit code:</strong> {outputRecord.exit_code ?? "(null)"}
            </p>

            <p>
              <strong>Raw command:</strong>
            </p>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                background: "#111",
                color: "#eee",
                padding: 8,
                borderRadius: 4,
              }}
            >
              {outputRecord.raw_command || ""}
            </pre>

            <p>
              <strong>Rendered command:</strong>
            </p>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                background: "#111",
                color: "#eee",
                padding: 8,
                borderRadius: 4,
              }}
            >
              {outputRecord.rendered_command || ""}
            </pre>

            <p>
              <strong>STDOUT:</strong>
            </p>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                background: "#000",
                color: "#0f0",
                padding: 8,
                borderRadius: 4,
              }}
            >
              {outputRecord.stdout || ""}
            </pre>

            <p>
              <strong>STDERR:</strong>
            </p>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                background: "#000",
                color: "#f55",
                padding: 8,
                borderRadius: 4,
              }}
            >
              {outputRecord.stderr || ""}
            </pre>

            <p>
              <strong>Error message:</strong>
            </p>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                background: "#111",
                color: "#ffb",
                padding: 8,
                borderRadius: 4,
              }}
            >
              {outputRecord.error_message || ""}
            </pre>
          </div>
        ) : (
          <p>No record selected.</p>
        )}
      </Drawer>
    </div>
  );
};
